/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import dayjs from 'dayjs';
import React from 'react';

import { DateInput, Dropdown } from '@mairistem/composants';
import { ListeQueryContext } from '@mairistem/liste';

import { useOptionsReferentiel } from '@/hooks';

interface FiltresListeActesProps {
  /** Référentiel des sous-types du type d'acte de la liste. */
  urlSousTypes: string;
  /**
   * Champ du référentiel qui porte le code stocké sur l'acte : `codeJustice`
   * pour les sous-types d'acte, `codeEtatCivil` pour les types de
   * reconnaissance.
   */
  cleCode?: string;
  /** Le tableau de la liste, rendu sous les filtres dans la hauteur restante. */
  children: React.ReactNode;
  'data-testid'?: string;
}

/**
 * Filtres des listes d'actes du menu de gauche (AD-12972) : sous-types d'acte
 * et date, cherchée par l'API dans la date dressée comme dans celle de
 * l'évènement. Les deux sont envoyés ensemble, `filterQuery` remplaçant les
 * filtres précédents.
 *
 * Rangés sous la recherche, au-dessus du tableau, comme les filtres de la
 * liste HubEE. `Liste.Content` occupe toute la hauteur sans défiler : les
 * filtres et le tableau s'y partagent la place en colonne.
 */
const FiltresListeActes = ({
  urlSousTypes,
  cleCode = 'codeJustice',
  children,
  'data-testid': dataTestId = 'filtres-liste-actes',
}: FiltresListeActesProps) => {
  const { filterQuery } = React.useContext(ListeQueryContext);
  const { options } = useOptionsReferentiel(urlSousTypes, cleCode);

  const [sousTypes, setSousTypes] = React.useState<string[]>([]);
  const [date, setDate] = React.useState<Date | undefined>();

  const filtrer = (nouveauxSousTypes: string[], nouvelleDate?: Date) => {
    filterQuery({
      ...(nouveauxSousTypes.length && {
        sous_type: nouveauxSousTypes.join(','),
      }),
      ...(nouvelleDate && { date: dayjs(nouvelleDate).format('YYYY-MM-DD') }),
    });
  };

  const handleSousTypesChange = (
    _e: React.SyntheticEvent,
    { value }: { value?: unknown },
  ) => {
    const valeurs = Array.isArray(value) ? value.map(String) : [];
    setSousTypes(valeurs);
    filtrer(valeurs, date);
  };

  const handleDateChange = (valeur: Date | string | undefined) => {
    const nouvelleDate =
      valeur && dayjs(valeur).isValid() ? dayjs(valeur).toDate() : undefined;

    // La saisie au clavier émet `undefined` tant que la date est incomplète :
    // pas de nouvelle requête si rien ne change.
    const inchangee =
      nouvelleDate && date
        ? dayjs(nouvelleDate).isSame(date, 'day')
        : !nouvelleDate && !date;

    if (inchangee) {
      return;
    }

    setDate(nouvelleDate);
    filtrer(sousTypes, nouvelleDate);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '20px',
          flexWrap: 'wrap',
          padding: '15px',
        }}
        data-testid={dataTestId}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontWeight: 'bold' }}>Filtrer sur le(s) type(s)</span>
          <Dropdown
            selection
            multiple
            clearable
            options={options}
            placeholder="Tous les types d'acte"
            style={{ minWidth: '16em' }}
            data-testid={`${dataTestId}-sous-type`}
            onChange={handleSousTypesChange}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontWeight: 'bold' }}>Dressé ou survenu le</span>
          <DateInput value={date} onChange={handleDateChange} />
        </div>
      </div>
      <div style={{ flex: 1, minHeight: 0 }}>{children}</div>
    </div>
  );
};

export default FiltresListeActes;
